import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { Row, Col, Divider, Button, Modal } from 'antd';
import SpaceManagementLayout from './SpaceManagementLayout';
import { fetchSpaceDetails } from './HostDashboardActions';
import SpaceDetailsLoader from '../Common/Loaders/SpaceDetailsLoader';
import { IMAGES_BASE_URL } from '../../settings';
import { getValidColoreForSpaceStatus } from '../../helper/StatusColor';
// Removed SpaceDetails.scss as it's now imported via index.scss in SpaceManagementLayout
import HostFooter from './HostFooter';
import useWindowDimensions from '../Common/Hooks/useWindowDimensions';
import HelpAndSupport from '../Static-pages/HelpAndSupport/HelpAndSupport';

// Import Icons
import guestsIcon from '../../assets/images/common/guests-blue.svg';
import roomSpaceIcon from '../../assets/images/common/roomSpace-blue.svg';
import noticePeriodIcon from '../../assets/images/common/noticePeriod.svg';
import calendarIcon from '../../assets/images/common/calendar-blue.svg';
import cancellationIcon from '../../assets/images/common/cancellationPolicy-blue.svg';
import parkingIcon from '../../assets/images/common/parkingAvailable.svg';
import checkIcon from '../../assets/images/common/check-icon.svg';
import locationIcon from '../../assets/images/common/location-blue.svg';
import securityDepositIcon from '../../assets/images/common/securityDeposit.svg';

const SpaceDetails = () => {
    const dispatch = useDispatch();
    const { spaceId, country } = useParams();
    const dimensions = useWindowDimensions();
    const mobileView = dimensions.width < 992;
    
    // Selectors
    const spaceDetails = useSelector((state) => state?.hostDashboard?.data?.spaceDetails || {});
    const fetching = useSelector(state => state?.hostDashboard?.fetching);
  
    // State
    const [isDescriptionExpanded, setDescriptionExpanded] = useState(false);
    const [showAllAmenities, setShowAllAmenities] = useState(false);
    const [visibleModel, setHelpAndSupportVisible] = useState(false);
    const [verifiedImages, setVerifiedImages] = useState([]);

    useEffect(() => {
      window.scrollTo(0, 0);
      if (spaceId) {
         dispatch(fetchSpaceDetails(spaceId));
      }
    }, [spaceId, dispatch]);

    useEffect(() => {
        if (spaceDetails?.images?.length > 0) {
            const checkImages = async () => {
                const promises = spaceDetails.images.map(img => {
                    return new Promise((resolve) => {
                        const image = new Image();
                        image.src = IMAGES_BASE_URL + img.url;
                        image.onload = () => resolve({ ...img, isValid: true });
                        image.onerror = () => resolve({ ...img, isValid: false });
                    });
                });

                const results = await Promise.all(promises);
                const validImages = results.filter(img => img.isValid);
                setVerifiedImages(validImages);
            };
            checkImages();
        } else {
            setVerifiedImages([]);
        }
    }, [spaceDetails]);
  
    if (fetching || !spaceDetails?.id) {
      return <SpaceDetailsLoader />;
    }

    // --- Data Extraction Helpers ---
    const event = spaceDetails?.events?.[0] || {};
    const categoryName = spaceDetails?.eventType?.name || event?.eventType?.name || 'Category';
    const address = `${spaceDetails?.location?.address || ''}, ${spaceDetails?.location?.city || ''}`;
    const status = spaceDetails?.spaceStatus;
    
    // AC4: Key Details
    // Logic from SpaceDescription.js
    const maxGuests = event?.seatArrangements?.[0]?.max ? `${event.seatArrangements[0].max} ${event.seatArrangements[0].max > 1 ? 'Guests' : 'Guest'}` : (spaceDetails?.maxHeadCount ? `${spaceDetails.maxHeadCount} Guests` : null);
    
    let roomSpace = null;
    if (spaceDetails?.spaceArea?.amount) {
         roomSpace = `${spaceDetails.spaceArea.amount} ${spaceDetails.spaceArea.unit || 'Sq.ft'}`;
    } else if (event?.spaceArea?.amount) {
         roomSpace = `${event.spaceArea.amount} ${event.spaceArea.unit || 'Sq.ft'}`;
    }

    const noticePeriod = event?.chargings?.find(value => value?.chargingType?.unit === 'hour' || value?.chargingType?.unit === 'month')?.noticePeriod;
    const noticePeriodDisplay = noticePeriod ? `${noticePeriod} hours` : null;
    
    // Minimum Booking Period Logic from SpaceProfile
    const minBookCharge = event?.chargings?.find(charge => charge.chargingType.name === 'Monthly' || charge.chargingType.name === 'Hourly');
    const minBookingValue = minBookCharge?.minimumBookingPeriod;
    const minBookingUnit = minBookCharge?.chargingType?.name === 'Monthly' ? (minBookingValue > 1 ? 'months' : 'month') : (minBookingValue > 1 ? 'hours' : 'hour');
    // Using SpaceDescription.js format
    const minBookingDisplay = (minBookingValue && minBookingValue !== 0) ? `${minBookingValue} ${minBookingUnit} (This host requires ${minBookCharge?.chargingType?.name === 'Monthly' ? 'monthly' : 'hourly'} bookings to be for a minimum period of ${minBookingValue} ${minBookingUnit})` : null;

    // Cancellation Policy Logic
    const cancellationPolicyName = event?.cancellationPolicy?.name;
    const cancellationPolicyDesc = event?.cancellationPolicy?.description; 
    const cancellationPolicyDisplay = cancellationPolicyName ? `${cancellationPolicyName} (${cancellationPolicyDesc})` : null;

    // Refundable Security Deposit Logic from SpaceProfile (SpaceDescription.js)
    const refundableDepositObj = event?.chargings?.find(value => value?.chargingType?.unit === 'month');
    const isRefundableDepositAvailable = refundableDepositObj?.refundableDepositAvailability;
    const refundableDepositDisplay = isRefundableDepositAvailable ? 
        `${refundableDepositObj?.refundableDeposit?.deposit?.currency || ''} ${refundableDepositObj?.refundableDeposit?.deposit?.amount || ''} / ${refundableDepositObj?.refundableDeposit?.months || ''} ${refundableDepositObj?.refundableDeposit?.months > 1 ? 'months' : 'month'} (To be paid directly to the host)` 
        : null;

    const parkingAvailable = spaceDetails?.parkingAvailable ? 'Yes' : null; 

    // AC5: Amenities
    const amenities = event?.amenities || spaceDetails?.amenities || [];
    const displayedAmenities = showAllAmenities ? amenities : amenities.slice(0, 6);

    // AC7: Rules
    const rules = event?.keepInMind || spaceDetails?.rules || [];
    // Ensure we handle the structure correctly: rule object is inside the array item under 'rule' property
    const displayedRules = rules.filter(r => !r.isDisabled).map(r => r.rule || r);

    // AC8: Operating Hours (grouped by charging type)
    const operatingHoursByChargingType = (event?.chargings || [])
        .map((charging, chargingIndex) => {
            const rows = (charging?.pricingOptions || []).map((opt, optionIndex) => ({
                key: `${charging?.chargingType?.name || 'charge'}-${chargingIndex}-${opt?.name || 'opt'}-${optionIndex}`,
                name: opt?.name || 'Available',
                time: charging?.access24by7 ? '24/7 Access' : `${opt?.startTime || '00:00'} - ${opt?.endTime || '00:00'}`,
            }));

            return {
                key: `${charging?.chargingType?.name || 'charge'}-${chargingIndex}`,
                chargingTypeName: charging?.chargingType?.name || 'Pricing',
                rows,
            };
        })
        .filter((group) => group.rows.length > 0);

    // AC9: Seating
    const seatingArrangement = event?.seatArrangements || spaceDetails?.seatingArrangement || [];

    const DetailItem = ({ icon, label, value }) => (
        <div className="detail-item">
            <img src={icon} alt={label} />
            <div className="detail-text">
                <span className="label">{label}:</span>
                <span className="value">{value}</span>
            </div>
        </div>
    );
    
    return (
        <SpaceManagementLayout activeTab="1">
            <div className="my-space-details host-dashboard">     
                <div className="space-details-container">
                    
                    {/* AC2 – Space Header Section */}
                    <div className="header-section">
                        <div className="title-row">
                            <div className="names-container">
                                <h1 className="space-name">{spaceDetails?.spaceName}</h1>
                                <span className="category-badge">{categoryName}</span>
                            </div>
                            <span className={`status-badge ${status} ${getValidColoreForSpaceStatus(status)}`}>{status}</span>
                        </div>
                        <div className="address">
                            {address}
                        </div>
                    </div>

                    {/* AC3 – Photo Gallery */}
                    <div className="photo-gallery">
                        {verifiedImages?.length > 0 ? (
                            <div className={`gallery-grid images-${Math.min(verifiedImages.length, 5)}`}>
                                {verifiedImages.slice(0, 5).map((img, index) => (
                                    <div 
                                        key={index}
                                        className={`gallery-item item-${index}`}
                                    >
                                        <img src={IMAGES_BASE_URL + img.url} alt={`Space ${index + 1}`} />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="no-images">No images uploaded</div>
                        )}
                    </div>

                    <Row gutter={48}>
                        <Col xs={24} md={24}>
                            {/* AC4 – Space Info / Key Details Section */}
                            <div className="details-section">
                                <div className="key-details">
                                    {maxGuests && <DetailItem icon={guestsIcon} label="Maximum guests" value={maxGuests} />}
                                    {cancellationPolicyDisplay && <DetailItem icon={cancellationIcon} label="Cancellation policy" value={cancellationPolicyDisplay} />}
                                    
                                    {minBookingDisplay && <DetailItem icon={calendarIcon} label="Minimum booking period" value={minBookingDisplay} />}
                                     
                                    {roomSpace && <DetailItem icon={roomSpaceIcon} label="Room space" value={roomSpace} />}
                                    
                                    {noticePeriodDisplay && <DetailItem icon={noticePeriodIcon} label="Notice period" value={noticePeriodDisplay} />}
                                    
                                    {refundableDepositDisplay && <DetailItem icon={securityDepositIcon} label="Refundable security deposit" value={refundableDepositDisplay} />}

                                    {parkingAvailable && <DetailItem icon={parkingIcon} label="Parking available" value={parkingAvailable} />}
                                </div>
                            </div>

                            {/* AC5 – "What This Place Offers" Section */}
                            <div className="details-section">
                                <h3>What this place offers</h3>
                                <div className="amenities-list">
                                    {displayedAmenities.length > 0 ? displayedAmenities.map((amenity, idx) => (
                                        <div key={idx} className="amenity-item">
                                            <img src={(amenity.icon ? IMAGES_BASE_URL + amenity.icon : checkIcon)} alt="amenity" onError={(e)=> {e.target.src = checkIcon}}/>
                                            <span>{amenity.name}</span>
                                        </div>
                                    )) : <p>No amenities listed.</p>}
                                </div>
                                {amenities.length > 6 && (
                                    <button className="show-more-btn" onClick={() => setShowAllAmenities(!showAllAmenities)}>
                                        {showAllAmenities ? 'Show less' : `Show all ${amenities.length} amenities`}
                                    </button>
                                )}
                            </div>

                            {/* AC6 – Place Description Section */}
                            <div className="details-section">
                                <h3>About this space</h3>
                                {spaceDetails?.description ? (
                                    <>
                                        <div className={`description-text ${!isDescriptionExpanded ? 'truncated' : ''}`} dangerouslySetInnerHTML={{ __html: spaceDetails.description }} />
                                        {spaceDetails.description.length > 300 && (
                                            <button className="read-more-link" onClick={() => setDescriptionExpanded(!isDescriptionExpanded)}>
                                                {isDescriptionExpanded ? 'Show less' : 'Show more'}
                                            </button>
                                        )}
                                    </>
                                ) : <p>No description provided.</p>}
                            </div>

                            {/* AC7 – Space Rules Section */}
                            <div className="details-section">
                                <h3>Space Rules</h3>
                                <div className="rules-list">
                                    {displayedRules.length > 0 ? displayedRules.map((rule, idx) => (
                                        <div key={idx} className="rule-item">
                                            <img src={rule.icon ? (IMAGES_BASE_URL + rule.icon) : checkIcon} alt="rule" />
                                            <span>{rule.invertedName || rule.name}</span>
                                        </div>
                                    )) : <p>No specific rules listed.</p>}
                                </div>
                            </div>
                            
                            {/* AC8 – Operational Hours Section */}
                            <div className="details-section operational-hours-card">
                                <h3>Operational Hours</h3>
                                <div className="hours-list">
                                    {operatingHoursByChargingType.length > 0 ? operatingHoursByChargingType.map((group) => (
                                        <div key={group.key} className="hours-card">
                                            <h4>{group.chargingTypeName}</h4>
                                            {group.rows.map((hour) => (
                                                <div key={hour.key} className="hours-row">
                                                    <img src={calendarIcon} alt="calendar" />
                                                    <span className="text"><b>{hour.name}:</b> {hour.time}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )) : (
                                        <p>Contact host for availability details.</p>
                                    )}
                                </div>
                            </div>
                        </Col>
                    </Row>
                    
                    {/* AC9 – Seating Arrangement Section (Bottom as per requested order implies or separate) */}
                    {(seatingArrangement?.length > 1 && seatingArrangement?.[0]?.seatArrangement) && (
                        <div className="details-section">
                            <h3>Seating Arrangements</h3>
                            <div className="seating-list gallery-grid">
                                    {seatingArrangement.map((seat, idx) => (
                                        <div key={idx} className="seat-card">
                                            <div className="seat-icon">
                                                <img 
                                                    src={IMAGES_BASE_URL + (seat?.seatArrangement?.icon || '')} 
                                                    alt={seat?.seatArrangement?.name || 'seat'} 
                                                    onError={(e) => {e.target.parentElement.style.display = 'none'}} 
                                                />
                                            </div> 
                                            <span className="seat-name">
                                                {seat?.seatArrangement?.name || seat?.layoutOption?.name || 'Layout'}
                                            </span>
                                            <span className="seat-count">
                                                Max: {seat.max || seat.capacity || seat.amount || seat.min || 0}
                                            </span>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {mobileView && <HostFooter />}
            <Modal title="Help & Support" className="help-dialog" visible={visibleModel} footer={null} onCancel={() => setHelpAndSupportVisible(false)}>
                <HelpAndSupport visibleHelpAndSupport={visibleModel} closeHelpAndSupport={() => setHelpAndSupportVisible(false)} />
            </Modal>
        </SpaceManagementLayout>
    );
};

export default SpaceDetails;
